import { useState } from "react";
import { LeftOutlined, RightOutlined, PlusCircleFilled } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Calendar, Button, Popover, Spin } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import ScheduleInterviewModal from "./Modal/ScheduleInterviewModal";
import InterviewHoverContent from "./Card/InterviewCard";
import { useInterview } from "../../app/hooks/useInterview";
import { observer } from "mobx-react-lite";

dayjs.extend(utc);

const INTERVIEW_TYPE_LABELS = {
  InitialInterview: "Initial Interview",
  TechnicalInterview: "Technical Interview",
  FinalInterview: "Final Interview",
  PanelInterview: "Panel Interview",
  HRInterview: "HR Interview",
};

const SchedulingBody = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [isModalVisible, setIsModalVisible] = useState(false);
  const startDate = currentDate.startOf("month").toISOString();
  const endDate = currentDate.endOf("month").toISOString();

  const { data, isLoading } = useInterview({
    startDate,
    endDate,
  });

  const getWeekDays = () => {
    const startOfWeek = currentDate.startOf("week");
    return Array.from({ length: 7 }, (_, i) => {
      const day = startOfWeek.add(i, "day");
      return {
        name: day.format("MMM").toUpperCase(),
        fullName: day.format("ddd").toUpperCase(),
        date: day.date(),
        isToday: day.isSame(dayjs(), "day"),
        fullDate: day.format("YYYY-MM-DD"),
      };
    });
  };

  const handlePrevMonth = () => {
    setCurrentDate(currentDate.subtract(1, "month"));
  };

  const handleNextMonth = () => {
    setCurrentDate(currentDate.add(1, "month"));
  };

  const handleToday = () => {
    setCurrentDate(dayjs());
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleSave = () => {
    setIsModalVisible(false);
  };

  const timeSlots = ["8 AM", "9 AM", "10 AM", "11 AM", "12 PM", "1 PM", "2 PM", "3 PM", "4 PM", "5 PM"];
  const weekDays = getWeekDays();
  const interviewData = Array.isArray(data) ? data : [];

  console.log(interviewData);

  return (
    <div className="scheduling-container min-h-screen font-sans">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <LeftOutlined onClick={() => navigate("/recruitment")} className="cursor-pointer p-2 text-2xl hover:bg-gray-200 rounded-full transition-all" />
          <h1 className="text-3xl font-bold text-gray-800">Schedule Interview</h1>
        </div>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-center gap-8 mb-8">
        <div className="flex flex-col sm:flex-row items-center gap-12">
          <Button size="large" onClick={handleToday} className="today-btn rounded-xl px-10 h-14 text-lg font-medium text-gray-500 border-gray-300">
            Today
          </Button>
          <div className="flex gap-4 text-xl text-gray-400">
            <LeftOutlined onClick={handlePrevMonth} className="cursor-pointer text-3xl hover:text-black transition-colors" />
            <RightOutlined onClick={handleNextMonth} className="cursor-pointer text-3xl hover:text-black transition-colors" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-800">{currentDate.format("MMMM YYYY")}</h2>
        </div>
        <Button
          type="primary"
          onClick={showModal}
          className="shadow-lg flex items-center rounded-2xl text-xl font-bold text-white py-8 px-6 gap-5"
          style={{ backgroundColor: "#FF7201", borderColor: "#FF7201" }}
        >
          <PlusCircleFilled style={{ fontSize: "40px" }} />
          ADD SCHEDULE
        </Button>
      </div>

      <Spin spinning={isLoading}>
        <div className="flex flex-wrap gap-8 items-start">
          <div className="w-80 bg-white p-4 rounded-2xl shadow-2xl border border-gray-100">
            <Calendar
              fullscreen={false}
              value={currentDate}
              onSelect={(date) => setCurrentDate(date)}
              headerRender={({ value }) => (
                <div className="flex items-center justify-between p-2 mb-2">
                  <span className="text-lg font-bold text-gray-700">{value.format("MMMM YYYY")}</span>
                  <div className="flex gap-4 text-gray-400">
                    <LeftOutlined onClick={handlePrevMonth} className="cursor-pointer hover:text-black" />
                    <RightOutlined onClick={handleNextMonth} className="cursor-pointer hover:text-black" />
                  </div>
                </div>
              )}
              className="custom-mini-calendar"
            />
          </div>

          <div className="flex-1 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden relative">
            <div className="grid grid-cols-8 border-b border-gray-100">
              <div className="p-4 text-xs font-bold text-gray-400 self-end text-center uppercase">Time</div>
              {weekDays.map((day, idx) => (
                <div key={idx} className={`p-4 text-center ${day.isToday ? "bg-orange-50/30" : ""}`}>
                  <div className="text-xs font-bold text-gray-400 uppercase">{day.fullName}</div>
                  <div className="text-3xl font-light text-gray-700">{day.date}</div>
                </div>
              ))}
            </div>

            <div className="relative p-2">
              {timeSlots.map((time) => (
                <div key={time} className="grid grid-cols-8 border-b border-gray-100 h-16">
                  <div className="text-[10px] font-bold text-gray-400 flex items-center justify-center border-r border-gray-100 uppercase bg-gray-50/20">
                    {time}
                  </div>
                  {weekDays.map((day, dayIdx) => {
                    const interviews = interviewData.filter((item) => {
                      if (!item.scheduledDate || !item.startTime) return false;

                      const itemDate = dayjs(item.scheduledDate).local().format("YYYY-MM-DD");

                      const itemHour = dayjs(`2000-01-01 ${item.startTime}`).hour();

                      const slotHour = dayjs(`2000-01-01 ${time}`, "YYYY-MM-DD h A").hour();

                      return itemDate === day.fullDate && itemHour === slotHour;
                    });

                    return (
                      <div key={dayIdx} className="border-r border-gray-100 relative group transition-colors hover:bg-gray-50/50">
                        {interviews.length === 1 && (
                          <Popover content={<InterviewHoverContent interview={interviews[0]} />} trigger="hover" placement="topLeft">
                            <div
                              className="absolute rounded-md p-1 cursor-pointer shadow-sm"
                              style={{
                                backgroundColor: "#FF9D4D",
                                color: "white",
                                border: "#FF9D4D",
                              }}
                            >
                              <div className="text-[10px] font-bold">{INTERVIEW_TYPE_LABELS[interviews[0].type] || interviews[0].type}</div>
                              <div className="text-[9px]">
                                {dayjs(`2000-01-01 ${interviews[0].startTime}`).format("h:mm A")}
                                {" - "}
                                {dayjs(`2000-01-01 ${interviews[0].endTime}`).format("h:mm A")}
                              </div>
                            </div>
                          </Popover>
                        )}

                        {interviews.length > 1 && (
                          <Popover
                            trigger="hover"
                            placement="topRight"
                            content={
                              <div className="flex gap-2 flex-row items-start max-w-[450px] overflow-x-auto">
                                {interviews.map((interview) => (
                                  <div key={interview.id} className="bg-white borderrounded-xl">
                                    <InterviewHoverContent interview={interview} />
                                  </div>
                                ))}
                              </div>
                            }
                          >
                            <div
                              className="absolute rounded-md p-2 cursor-pointer shadow-sm flex items-center justify-center"
                              style={{
                                backgroundColor: "#FF9D4D",
                                color: "white",
                                left: "4px",
                                right: "4px",
                                fontSize: "10px",
                                fontWeight: "bold",
                                textAlign: "center",
                              }}
                            >
                              {interviews.length} interviews
                            </div>
                          </Popover>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </Spin>
      <ScheduleInterviewModal isVisible={isModalVisible} onCancel={handleCancel} onSave={handleSave} />
    </div>
  );
};

export default observer(SchedulingBody);
